// 261015 P2b-1 Bước 8 — hộp "Chấp nhận cảnh báo, có lý do" (C6). Mọi quyết định (đích, lý do hợp lệ, dựng lệnh) ở `skeletonAcceptView.ts` (hàm thuần, có jest).
// BE tự lấy người bấm từ token và đóng dấu giờ + dấu nội dung; FE chỉ gửi {gate, ref, reason}.
import React, { useEffect, useState } from 'react';
import { Checkbox, Input, Modal, Space, Typography } from 'antd';
import type { OutlinePatchOp } from '../../../types/adaptation';
import { gateLabel, humanizeCodes } from '../adaptationUtils';
import { ACCEPT_REASON_INPUT_MAX, acceptReasonState, acceptTargets, buildAcceptOps, canSubmitAccept, isAcceptableGate } from './skeletonAcceptView';

const { Text } = Typography;

export interface AcceptTarget { gate: string; refs: string[]; message: string }

interface Props {
  target: AcceptTarget | null;
  busy: boolean;
  nameOf?: (id: string) => string;
  onCancel: () => void;
  /** Nhận danh sách lệnh (một `acceptWarning` mỗi ref đã chọn) — caller chạy qua luồng xem thử → PATCH. */
  onSubmit: (ops: OutlinePatchOp[]) => void | Promise<void>;
}

const AcceptWarningModal: React.FC<Props> = ({ target, busy, nameOf, onCancel, onSubmit }) => {
  const [reason, setReason] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const targets = target ? acceptTargets({ gate: target.gate, refs: target.refs }) : [];
  useEffect(() => { setReason(''); setPicked(target ? acceptTargets({ gate: target.gate, refs: target.refs }) : []); }, [target?.gate, target?.message]); // eslint-disable-line react-hooks/exhaustive-deps
  const gate = target?.gate;
  if (!target || !isAcceptableGate(gate)) return null;
  const state = acceptReasonState(reason);
  const ok = canSubmitAccept(gate, picked, reason);
  const isGap = gate === 'BEAT_GAP';
  return (
    <Modal
      open title="Chấp nhận cảnh báo, có lý do" okText="Chấp nhận" cancelText="Thôi" onCancel={onCancel}
      okButtonProps={{ disabled: !ok || busy, 'data-testid': 'accept-ok' } as never} confirmLoading={busy}
      onOk={() => { if (ok) onSubmit(buildAcceptOps(gate, picked, reason)); }}
    >
      <Space direction="vertical" size={10} style={{ width: '100%' }}>
        <Text strong>{gateLabel(gate)}</Text>
        <Text type="secondary" style={{ fontSize: 13 }}>{humanizeCodes(target.message, nameOf)}</Text>
        <Text type="secondary" style={{ fontSize: 13 }}>
          Cảnh báo KHÔNG bị xoá: nó chuyển sang mục “Đã chấp nhận” kèm lý do, người chấp nhận và thời gian. Nếu nội dung liên quan đổi sau đó, cảnh báo sẽ hiện lại.
        </Text>
        {!isGap && targets.length > 0 && (
          <div>
            <Text>Chấp nhận cho:</Text>
            <div>
              <Checkbox.Group
                value={picked} onChange={(v) => setPicked(v as string[])}
                options={targets.map((r) => ({ label: nameOf ? nameOf(r) : r, value: r }))}
              />
            </div>
          </div>
        )}
        <div>
          <Input.TextArea
            value={reason} onChange={(e) => setReason(e.target.value)} rows={3} maxLength={ACCEPT_REASON_INPUT_MAX} showCount
            placeholder={isGap ? 'Vì sao khoảng trống này là nhịp thật của nguyên tác, không còn vật liệu bổ sung?' : 'Vì sao cố ý để như vậy? (vd: gieo ngay trong cảnh nổ)'}
            aria-label="Lý do chấp nhận"
          />
          {state.hint && reason.length > 0 && <Text type="danger" style={{ fontSize: 13 }}>{state.hint}</Text>}
        </div>
      </Space>
    </Modal>
  );
};

export default AcceptWarningModal;
